<template lang="html">

  <div :class="['pe_round', {'pe_finished': correctChosen && !isFinalRound }]">

      <div class="pe_question">
          <h2>{{ question }}</h2>
          <div class="pe_timer">
            <span :class="{'pe_pause': correctChosen }"></span>
          </div>
      </div>

    <div class="pe_choices">
      <button
        v-for="option in options"
        :class="['pe_btn pe_primary pe_answer', {'pe_was-pressed': option.wasPressed, 'pe_correct': option.wasPressed && option.isAnswer }]"
        @click="onClick(option)"
      >
        <div class="pe_left">
          <Icon 
            :isTick="option.isAnswer" 
            :class="['pe_drawTick', {'pe_drawn': option.wasPressed }]" 
          /> 
        </div>
        <span class="pe_right">{{ option.choice }}</span> 
      </button>
    </div>

  </div>

</template>

<script lang="js">

  import { mapActions, mapState, mapGetters } from 'vuex'
  import { shuffle, normalize } from '@puzzleapps/shared/src/utils'
  import Icon from './Icon'

  export default  {
    name: 'grid',
    props: ['question', 'answer', 'choices', 'isFinalRound'],

    components: {
      Icon
    },

    data: () => ({
      options: [],
      score: 0,
      correctChosen: false,
      timestamp: null,
      totalTime: 12,// that the css animation time must match the integer here
    }),

    mounted() {
      let choices = this.choices.map(opt => this.createOption(opt, false))
      choices.push(this.createOption(this.answer, true))
      this.options = shuffle(choices)
      this.score = this.options.length
      this.timestamp = new Date()
    },

    destroyed() {
      this.options = []
      this.timestamp = null
    },

    methods: {
      ...mapActions({
        addPoints: 'game/addPoints',
        incrementCorrectOnFirst: 'game/incrementCorrectOnFirst',
      }),

      getElapsedTime() {
        return Math.min(Math.round((Date.now() - this.timestamp) / 1000), this.totalTime)
      },

      createOption(choice, isAnswer) {
        return { choice, isAnswer, wasPressed: false }
      },

      // this is a score from 1-4 multiplied by the elapsed time
      roundScore(score) {
        //score = the number of options (this is 4 in this game) minus the number of incorrect guesses
        //Jeff - I have modded this to reward first correct answers more ie the score * 10 below
        // 7th June 2023 - I add + 1 to the timeMultiplier so that you always get a score even if time runs out
        // also changed to * 2.5 as this seems to create a realistic score where 1000 is a very good score 
       

        const timeMultiplier = normalize(this.getElapsedTime(), 0, this.totalTime) * 2.8 + 1

        return Math.round((score * 10) * timeMultiplier)
      },

      correctOnFirst() {
        return this.score === this.options.length
      },

      onClick(option) {
        if (this.correctChosen) return

        option.wasPressed = true
        
        if (option.isAnswer)
        {
          if (this.correctOnFirst())
            this.incrementCorrectOnFirst()
          this.addPoints(this.roundScore(this.score))
          this.correctChosen = true
          this.$emit('nextRound')
        }
        else
        {
          this.score--
        }
      }
    },
    
    computed: {
      ...mapState({
      }),
    }
}


</script>

<style scoped lang="scss">

  @import '@puzzleapps/shared/src/style/variables';

#puzzleapp.pe_trivia {

  @keyframes bounce-in {
    0% {
      transform: scale(0) translateY(0px);
    }
    50% {
      transform: scale(1.1) translateY(0px);
    }
    100% {
      transform: scale(1) translateY(0px);
    }
  }

  @keyframes slide-in {
    0% {
      opacity: 0;
      transform: rotate(30deg) scale(1) translateX(80%);
    }
    100% {
      opacity: 1;
      transform: rotate(0) scale(1) translateY(0px);
    }
  }

  @keyframes slide-out {
    0% {
      transform: rotate(0) scale(1) translateX(0);
      opacity: 1;
    }
    100% {
      // transform: rotate(-30deg) scale(1) translateX(-80%);
      opacity: 0;
    }
  }

  .fade-leave-active,
  .fade-enter-active {
    transition: all 0.5s cubic-bezier(1.0, 0.5, 0.8, 1.0)!important;
  }
  
  .fade-leave-to,
  .fade-enter, 
  .slide-fade-leave-to {
    opacity: 0;
  }

  .pe_round {
    padding: 1em;
    margin-bottom: 3em;
    text-align: center;
    max-width: 100%;
    animation: slide-in .7s;
    animation-fill-mode: forwards;
    position: absolute;
    transition: all 0.5s cubic-bezier(1.0, 0.5, 0.8, 1.0)!important;
    width: 100%;
    box-sizing: border-box;

    &.pe_finished {
      animation: slide-out 0.4s;
      animation-delay: 0.8s;
      animation-fill-mode: forwards;
      animation-timing-function: cubic-bezier(1.0, 0.5, 0.8, 1.0);
    }

    @media (max-width: 550px) {
      padding: 0;
    }
  }

  .pe_timer {
    display: block;
    width: 100%;
    height: 14px;
    border-radius:6px;
    overflow: hidden;
    position: absolute;
    bottom: 0%;
    left: 0;
    background: #fff;

    span {
      background: #000;
      display: block;
      animation: timeout 12s;
      animation-fill-mode: forwards;
      animation-timing-function: linear;
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;

      &.pe_pause {
        animation-play-state: paused;
      }
    }
  }

  @keyframes timeout {
    0% {
      background: #39e977;
      transform: translateX(-100%);
    }
    50% {
      background: #ffc130;
      // transform: translateX(-100%);
    }
    100% {
      background: #ff3030;
      transform: translateX(0);
    }
  }

  .pe_question {
    padding: 1em 1em 2em;
    margin: 0em 0 2em;
    color: #fff;
    background: transparent;
    border-radius: 6px;
    //box-shadow: 0 0 8px #00000005;
    position: relative;
    overflow: hidden;

     & > h2 {
       font-family: $pen-font!important;
       color:#fff!important;
       font-weight: 500;
     }

  }
  
  .pe_choices {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 2em;

    @media (max-width: 620px) {
      grid-template-columns: 1fr;
      grid-gap: 1em;
    }
  }

  .pe_drawTick {
    // margin-right: 1em;
  }

  .pe_btn.pe_primary.pe_answer {
    transform: scale(0) translateY(0px);
    animation: bounce-in .7s;
    animation-fill-mode: forwards;
    height: 90px;
    padding: 0;
    border-bottom: 2px solid rgba(0,0,0,0.13);
    border-radius: 7px;
    color: #fff;
    //background-image: linear-gradient(135deg, #4b29ab, #7f55ff);
    background-color: #3759d2;
    position: relative;
    display: flex;
    overflow: hidden;
    justify-content: left;

    .pe_left {
      padding: 1em;
      background: rgba(0,0,0,0.1);
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .pe_right {
      padding: 1em;
      // background: rgba(0,0,0,0.2);
      text-align: left;
    }

    @media (max-width: 620px) {
      height: 60px;
    }

    animation-delay: 0.6s;
    &:nth-of-type(2) { animation-delay: 0.8s;}
    &:nth-of-type(3) { animation-delay: 1s;}
    &:nth-of-type(4) { animation-delay: 1.2s;}

    &:active {
      border-bottom: 1px solid #3759d2;
      transition: all 0.0s;
      transform: scale(1) translateY(2px)!important;
      //background-image: linear-gradient(160deg, #4b29ab, #7f55ff)!important;
    }

    &.pe_was-pressed {
      background: #ff5555;
      //background-image: linear-gradient(160deg, #ab2929, #ff5555) !important;
      border: 2px solid rgba(0,0,0,0.13);
    }

    &.pe_was-pressed.pe_correct {
      background: #39e977;
      color:#000;
      //background-image: linear-gradient(160deg, #18783a, #39e977) !important;
      border-bottom: 2px solid rgba(0,0,0,0.13);
    }

  }

}

</style>